import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useForm } from 'react-hook-form'
import { ArrowLeft, Upload, FileText, X, Loader2, Sparkles, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { startupsApi } from '@/services/api'
import { formatFileSize, cn } from '@/utils'
import { StartupSubmissionForm } from '@/types'
import { subscriptionApi } from '@/services/api'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

interface DropDocsForm {
  name: string
  website: string
  industry: string
  contact_email: string
  description: string
}

export function DropDocsSubmissionPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: subscription, isLoading: checkingCredits } = useQuery({
    queryKey: ['subscription-current'],
    queryFn: subscriptionApi.getCurrent,
  })

  const limitReached = subscription?.usage && !subscription.usage.allowed

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<DropDocsForm>()

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const validFiles = acceptedFiles.filter(file => {
        if (file.size > 50 * 1024 * 1024) {
          toast({
            title: 'File too large',
            description: `${file.name} exceeds 50MB limit`,
            variant: 'destructive',
          })
          return false
        }
        return true
      })
      setUploadedFiles(prev => [...prev, ...validFiles])
    },
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/plain': ['.txt'],
    },
    maxSize: 50 * 1024 * 1024,
  })

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const base64 = reader.result as string
        const base64Data = base64.split(',')[1]
        resolve(base64Data)
      }
      reader.onerror = error => reject(error)
    })
  }

  const onSubmit = async (data: DropDocsForm) => {
    if (uploadedFiles.length === 0) {
      toast({
        title: "No documents attached",
        description: "Please upload at least one document (Pitch Deck, etc.) to proceed.",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)
    try {
      const documentsData = await Promise.all(
        uploadedFiles.map(async (file) => ({
          filename: file.name,
          content_type: file.type,
          data: await fileToBase64(file),
          size: file.size,
        }))
      )

      const submissionData: StartupSubmissionForm = {
        company_info: {
          name: data.name,
          website_url: data.website,
          industry: data.industry,
          description: data.description,
          // Defaults for required fields
          funding_stage: 'pre_seed',
          product_stage: 'idea',
          company_structure: 'private_limited',
          incorporation_location: 'Unknown',
          founded_date: '',
          location: 'Unknown',
          founders: [{
            name: 'Contact Person',
            email: data.contact_email,
            role: 'Founder',
            bio: '',
            previous_experience: [],
            education: []
          }],
          metadata: {
            key_metrics: {},
            competitive_advantages: [],
            traction_highlights: [],
          }
        },
        documents: documentsData,
      }

      const response = await startupsApi.create(submissionData)
      toast({
        title: 'Success!',
        description: 'Documents submitted successfully. Analysis will start shortly.',
      })
      queryClient.invalidateQueries({ queryKey: ['my-analyses'] })
      navigate('/dashboard')

    } catch (error) {
      console.error(error)
      toast({
        title: 'Submission failed',
        description: error instanceof Error ? error.message : 'Failed to submit documents',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (limitReached) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 md:p-12 flex items-center justify-center">
        <Card className="max-w-2xl w-full border-red-200 dark:border-red-900 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-red-600 dark:text-red-400">Analysis Limit Reached</CardTitle>
            <CardDescription className="text-lg mt-2">
              You have used all your available analyses for the current billing period.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600 dark:text-slate-300">
              Upgrade your plan to continue analyzing more startups immediately.
            </p>
          </CardContent>
          <CardFooter className="flex justify-center gap-4">
            <Link to="/plans">
              <Button size="lg" className="bg-logo-gradient text-white">
                View Plans & Upgrade
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" size="lg">
                Back to Dashboard
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/investor-submission')}>
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white">Drop the Docs</h1>
            <p className="text-slate-500 dark:text-slate-400">Quick submission by uploading documents and providing basic details.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

          {/* Documents Upload Section */}
          <Card className="border-2 border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <CardHeader className="bg-purple-50 dark:bg-purple-900/20 border-b border-purple-100 dark:border-purple-900/50">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg text-purple-600 dark:text-purple-400">
                        <Upload className="w-6 h-6" />
                    </div>
                    <div>
                        <CardTitle className="text-xl">Upload Documents</CardTitle>
                        <CardDescription>Pitch decks, business plans, or financial models</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-6">
                <div
                  {...getRootProps()}
                  className={cn(
                    'border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all',
                    isDragActive
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-slate-300 dark:border-slate-700 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  )}
                >
                  <input {...getInputProps()} />
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-4 bg-white dark:bg-slate-800 rounded-full shadow-sm">
                      <Upload className="w-8 h-8 text-purple-500" />
                    </div>
                    <div>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                        {isDragActive ? 'Drop files now' : 'Click or Drag files here'}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        PDF, PPTX, DOCX, XLSX (Max 50MB)
                        </p>
                    </div>
                  </div>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <h4 className="font-medium text-sm text-slate-900 dark:text-white">Attached Files ({uploadedFiles.length})</h4>
                    <div className="grid gap-3">
                        {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm group">
                            <div className="flex items-center gap-3 overflow-hidden">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-900 flex items-center justify-center shrink-0">
                                <FileText className="w-5 h-5 text-slate-500" />
                            </div>
                            <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{file.name}</p>
                                <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                            </div>
                            </div>
                            <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => removeFile(index)}
                            >
                            <X className="w-4 h-4" />
                            </Button>
                        </div>
                        ))}
                    </div>
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Basic Details Section */}
          <Card className="border-2 border-slate-200 dark:border-slate-800 shadow-sm">
             <CardHeader className="bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
                <CardTitle className="text-xl">Basic Information</CardTitle>
                <CardDescription>Provide minimal details to help us identify the startup</CardDescription>
            </CardHeader>
            <CardContent className="p-6 grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g. Acme Corp"
                    {...register('name', { required: 'Company name is required' })}
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry *</Label>
                  <Select onValueChange={(val) => setValue('industry', val)}>
                    <SelectTrigger className={!errors.industry ? '' : 'border-red-500'}>
                      <SelectValue placeholder="Select Industry" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="fintech">FinTech</SelectItem>
                        <SelectItem value="healthtech">HealthTech</SelectItem>
                        <SelectItem value="edtech">EdTech</SelectItem>
                        <SelectItem value="enterprise_software">Enterprise Software</SelectItem>
                        <SelectItem value="consumer_apps">Consumer Apps</SelectItem>
                        <SelectItem value="ecommerce">E-commerce</SelectItem>
                        <SelectItem value="ai_ml">AI/ML</SelectItem>
                        <SelectItem value="blockchain">Blockchain</SelectItem>
                        <SelectItem value="climate_tech">Climate Tech</SelectItem>
                        <SelectItem value="biotech">BioTech</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website (Optional)</Label>
                  <Input
                    id="website"
                    placeholder="https://..."
                    {...register('website')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="founder@startup.com"
                    {...register('contact_email', { required: 'Contact email is required' })}
                    className={errors.contact_email ? 'border-red-500' : ''}
                  />
                   {errors.contact_email && <p className="text-xs text-red-500">{errors.contact_email.message}</p>}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Short Description *</Label>
                  <Input
                    id="description"
                    placeholder="Briefly describe what the startup does..."
                    {...register('description', { required: 'Description is required' })}
                    className={errors.description ? 'border-red-500' : ''}
                  />
                   {errors.description && <p className="text-xs text-red-500">{errors.description.message}</p>}
                </div>
            </CardContent>
          </Card>

          {/* Submit Action */}
          <div className="flex items-center justify-end gap-4 pt-4">
             <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => navigate('/investor-submission')}
              >
                Cancel
              </Button>
            <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="bg-logo-gradient text-white border-none shadow-lg hover:opacity-90 min-w-[150px]"
            >
                {isSubmitting ? (
                    <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Uploading...
                    </>
                ) : (
                    <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Submit for Analysis
                    </>
                )}
            </Button>
          </div>

        </form>
      </div>
    </div>
  )
}
